
import React from 'react';
import { Language } from '../types';
import { Check, Globe } from 'lucide-react';
import { Button } from './ui/Button';

interface LanguageSelectionProps {
  currentLanguage: Language;
  onSelect: (lang: Language) => void;
  onConfirm: () => void;
}

export const LanguageSelection: React.FC<LanguageSelectionProps> = ({ currentLanguage, onSelect, onConfirm }) => {
  return (
    <div className="min-h-screen w-full bg-white flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 py-8 relative overflow-hidden safe-top safe-bottom">
      <div className="absolute top-0 inset-x-0 h-64 bg-gradient-to-b from-blue-50 to-transparent pointer-events-none"></div>

      {/* Logo and Title - Fully Centered */}
      <div className="mb-8 sm:mb-10 md:mb-12 text-center relative z-10 animate-slideDown w-full max-w-md">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-2xl sm:rounded-3xl mb-4 sm:mb-6 shadow-2xl rotate-3">
          <Globe className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Select Language</h1>
        <p className="text-slate-400 mt-2 text-[9px] sm:text-[10px] font-black tracking-[0.2em] sm:tracking-[0.3em] uppercase">अपनी भाषा चुनें</p>
      </div>

      {/* Language Options - Centered */}
      <div className="grid gap-3 sm:gap-4 w-full max-w-[320px] sm:max-w-xs relative z-10 px-4 sm:px-0">
        <button
          onClick={() => onSelect('en')}
          className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all flex items-center justify-between group ${currentLanguage === 'en' ? 'border-blue-600 bg-blue-50/50 shadow-lg' : 'border-slate-100 bg-white hover:border-blue-200'}`}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-sm ${currentLanguage === 'en' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>A</div>
            <div>
              <h3 className="font-black text-slate-900 text-sm sm:text-base text-left">English</h3>
              <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold uppercase tracking-widest text-left">Default Language</p>
            </div>
          </div>
          {currentLanguage === 'en' && <div className="bg-blue-600 text-white p-1 rounded-full"><Check className="w-3 h-3 sm:w-4 sm:h-4" /></div>}
        </button>

        <button
          onClick={() => onSelect('hi')}
          className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all flex items-center justify-between group ${currentLanguage === 'hi' ? 'border-blue-600 bg-blue-50/50 shadow-lg' : 'border-slate-100 bg-white hover:border-blue-200'}`}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-sm ${currentLanguage === 'hi' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>अ</div>
            <div>
              <h3 className="font-black text-slate-900 text-sm sm:text-base text-left">हिन्दी</h3>
              <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold uppercase tracking-widest text-left">Hindi Language</p>
            </div>
          </div>
          {currentLanguage === 'hi' && <div className="bg-blue-600 text-white p-1 rounded-full"><Check className="w-3 h-3 sm:w-4 sm:h-4" /></div>}
        </button>
      </div>

      {/* Continue Button - Centered */}
      <div className="mt-8 sm:mt-10 md:mt-12 w-full max-w-[320px] sm:max-w-xs relative z-10 px-4 sm:px-0">
        <Button fullWidth size="lg" onClick={onConfirm} className="h-14 sm:h-16 rounded-2xl sm:rounded-[1.5rem] text-sm sm:text-base font-black">
          {currentLanguage === 'en' ? 'Continue' : 'जारी रखें'}
        </Button>
      </div>
    </div>
  );
};
